/**
 * @file lib/git/gitlab-client.ts
 * @description GitLab API クライアント
 * @author GFTD Co., Ltd.
 */

import { Gitlab } from '@gitbeaker/browser';

/**
 * GitLab設定インターフェース
 */
export interface GitLabConfig {
  /** GitLabのURL */
  url: string;
  /** Personal Access Token */
  token: string;
  /** デフォルトプロジェクトの可視性 */
  defaultVisibility?: 'private' | 'internal' | 'public';
}

/**
 * プロジェクト作成設定
 */
export interface CreateProjectConfig {
  /** プロジェクト名 */
  name: string;
  /** 説明 */
  description?: string;
  /** 可視性 */
  visibility?: 'private' | 'internal' | 'public';
  /** 初期化設定 */
  initialize_with_readme?: boolean;
  /** デフォルトブランチ */
  default_branch?: string;
}

/**
 * ブランチ作成設定
 */
export interface CreateBranchConfig {
  /** ブランチ名 */
  name: string;
  /** 基点となるブランチまたはコミット */
  ref: string;
}

/**
 * GitLab APIクライアント
 */
export class GitLabClient {
  private client: InstanceType<typeof Gitlab>;
  private config: GitLabConfig;

  constructor(config: GitLabConfig) {
    this.config = config;
    this.client = new Gitlab({
      host: config.url,
      token: config.token,
    });
  }

  /**
   * 新しいプロジェクト（Actor）を作成
   * @param config プロジェクト作成設定
   * @returns プロジェクト情報
   */
  async createActor(config: CreateProjectConfig) {
    try {
      const project = await this.client.Projects.create({
        name: config.name,
        description: config.description || `Actor: ${config.name}`,
        visibility: config.visibility || this.config.defaultVisibility || 'private',
        initialize_with_readme: config.initialize_with_readme ?? true,
        default_branch: config.default_branch || 'main',
      });

      return {
        id: project.id,
        name: project.name,
        description: project.description,
        clone_url: project.http_url_to_repo,
        ssh_url: project.ssh_url_to_repo,
        web_url: project.web_url,
        default_branch: project.default_branch,
        created_at: project.created_at,
      };
    } catch (error) {
      console.error('Failed to create actor project:', error);
      throw error;
    }
  }

  /**
   * プロジェクトを取得
   * @param projectId プロジェクトID
   * @returns プロジェクト情報
   */
  async getActor(projectId: number | string) {
    try {
      const project = await this.client.Projects.show(projectId);
      
      return {
        id: project.id,
        name: project.name,
        description: project.description,
        clone_url: project.http_url_to_repo,
        ssh_url: project.ssh_url_to_repo,
        web_url: project.web_url,
        default_branch: project.default_branch,
        created_at: project.created_at,
        updated_at: project.last_activity_at,
      };
    } catch (error) {
      console.error('Failed to get actor project:', error);
      throw error;
    }
  }

  /**
   * 新しいブランチ（Session）を作成
   * 概念: new session -> new_branch
   * @param projectId プロジェクトID
   * @param config ブランチ作成設定
   * @returns ブランチ情報
   */
  async createSession(projectId: number | string, config: CreateBranchConfig) {
    try {
      const branch = await this.client.Branches.create(projectId, config.name, config.ref);

      return {
        name: branch.name,
        commit: {
          id: branch.commit.id,
          message: branch.commit.message,
          author_name: branch.commit.author_name,
          author_email: branch.commit.author_email,
          created_at: branch.commit.created_at,
        },
        protected: branch.protected,
        can_push: branch.can_push,
        web_url: branch.web_url,
      };
    } catch (error) {
      console.error('Failed to create session branch:', error);
      throw error;
    }
  }

  /**
   * ブランチ（Session）を取得
   * 概念: session = Git branch
   * @param projectId プロジェクトID
   * @param branchName ブランチ名（Session ID）
   * @returns ブランチ情報
   */
  async getSession(projectId: number | string, branchName: string) {
    try {
      const branch = await this.client.Branches.show(projectId, branchName);
      
      return {
        name: branch.name,
        commit: {
          id: branch.commit.id,
          message: branch.commit.message,
          author_name: branch.commit.author_name,
          author_email: branch.commit.author_email,
          created_at: branch.commit.created_at,
        },
        protected: branch.protected,
        can_push: branch.can_push,
        web_url: branch.web_url,
      };
    } catch (error) {
      console.error('Failed to get session branch:', error);
      throw error;
    }
  }

  /**
   * プロジェクトの全ブランチ（Sessions）を取得
   * 概念: sessions = Git branches
   * @param projectId プロジェクトID
   * @returns ブランチ一覧（Session一覧）
   */
  async listSessions(projectId: number | string) {
    try {
      const branches = await this.client.Branches.all(projectId);
      
      return branches.map(branch => ({
        name: branch.name,
        commit: {
          id: branch.commit.id,
          message: branch.commit.message,
          author_name: branch.commit.author_name,
          author_email: branch.commit.author_email,
          created_at: branch.commit.created_at,
        },
        protected: branch.protected,
        can_push: branch.can_push,
        web_url: branch.web_url,
      }));
    } catch (error) {
      console.error('Failed to list session branches:', error);
      throw error;
    }
  }

  /**
   * ブランチを削除
   * @param projectId プロジェクトID
   * @param branchName ブランチ名
   */
  async deleteSession(projectId: number | string, branchName: string) {
    try {
      await this.client.Branches.remove(projectId, branchName);
      return true;
    } catch (error) {
      console.error('Failed to delete session branch:', error);
      throw error;
    }
  }

  /**
   * プロジェクトを削除
   * @param projectId プロジェクトID
   */
  async deleteActor(projectId: number | string) {
    try {
      await this.client.Projects.remove(projectId);
      return true;
    } catch (error) {
      console.error('Failed to delete actor project:', error);
      throw error;
    }
  }

  /**
   * ファイルを作成または更新
   * @param projectId プロジェクトID
   * @param filePath ファイルパス
   * @param content ファイル内容
   * @param commitMessage コミットメッセージ
   * @param branch ブランチ名
   */
  async createOrUpdateFile(
    projectId: number | string,
    filePath: string,
    content: string,
    commitMessage: string,
    branch: string = 'main'
  ): Promise<any> {
    try {
      // ファイルが存在するかチェック
      let fileExists = false;
      try {
        await this.client.RepositoryFiles.show(projectId, filePath, branch);
        fileExists = true;
      } catch (error) {
        // ファイルが存在しない場合
        fileExists = false;
      }

      if (fileExists) {
        // ファイルを更新
        return await this.client.RepositoryFiles.edit(projectId, filePath, branch, content, commitMessage);
      } else {
        // ファイルを作成
        return await this.client.RepositoryFiles.create(projectId, filePath, branch, content, commitMessage);
      }
    } catch (error) {
      console.error('Failed to create or update file:', error);
      throw error;
    }
  }

  /**
   * ファイルを取得
   * @param projectId プロジェクトID
   * @param filePath ファイルパス
   * @param branch ブランチ名
   */
  async getFile(
    projectId: number | string,
    filePath: string,
    branch: string = 'main'
  ): Promise<any> {
    try {
      const file = await this.client.RepositoryFiles.show(projectId, filePath, branch);
      
      // Base64でエンコードされている場合はデコード
      const content = file.encoding === 'base64' 
        ? atob(file.content)
        : file.content;

      return {
        file_name: file.file_name,
        file_path: file.file_path,
        size: file.size,
        encoding: file.encoding,
        content,
        content_sha256: file.content_sha256,
        ref: file.ref,
        blob_id: file.blob_id,
        commit_id: file.commit_id,
        last_commit_id: file.last_commit_id,
      };
    } catch (error) {
      console.error('Failed to get file:', error);
      throw error;
    }
  }

  /**
   * ファイルを削除
   * @param projectId プロジェクトID
   * @param filePath ファイルパス
   * @param commitMessage コミットメッセージ
   * @param branch ブランチ名
   */
  async deleteFile(
    projectId: number | string,
    filePath: string,
    commitMessage: string,
    branch: string = 'main'
  ): Promise<any> {
    try {
      return await this.client.RepositoryFiles.remove(projectId, filePath, branch, commitMessage);
    } catch (error) {
      console.error('Failed to delete file:', error);
      throw error;
    }
  }

  /**
   * リポジトリのファイル一覧を取得
   * @param projectId プロジェクトID
   * @param path パス（オプション）
   * @param branch ブランチ名
   */
  async listFiles(
    projectId: number | string,
    path: string = '',
    branch: string = 'main'
  ): Promise<any[]> {
    try {
      const tree = await this.client.Repositories.tree(projectId, {
        path,
        ref: branch,
        recursive: true,
      });

      return tree.map(item => ({
        id: item.id,
        name: item.name,
        type: item.type,
        path: item.path,
        mode: item.mode,
      }));
    } catch (error) {
      console.error('Failed to list files:', error);
      throw error;
    }
  }
}

export default GitLabClient; 